/* Planilhas para download (CSV e .xlsx) geradas no navegador, sem biblioteca.
 *
 * O .xlsx é um zip de XMLs (Office Open XML). Aqui ele sai "armazenado" (sem compressão):
 * o arquivo fica maior, mas o Excel, o LibreOffice e o Google Planilhas abrem igual, e não
 * entra no bundle uma dependência inteira (SheetJS/ExcelJS) para exportar uma tabela.
 * Texto vai como inlineStr — sem tabela de strings compartilhadas, que é otimização.
 */

function utf8(texto) {
  const bytes = [];
  for (const ch of String(texto)) {
    let c = ch.codePointAt(0);
    if (c < 0x80) bytes.push(c);
    else if (c < 0x800) bytes.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else bytes.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(bytes);
}

let tabelaCrc = null;
function crc32(bytes) {
  if (!tabelaCrc) {
    tabelaCrc = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      tabelaCrc[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) crc = tabelaCrc[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** Zip sem compressão. `arquivos`: [{ nome, dados: Uint8Array }]. */
function zipArmazenado(arquivos, agora = new Date()) {
  const hora = (agora.getHours() << 11) | (agora.getMinutes() << 5) | (agora.getSeconds() >> 1);
  const dia = ((agora.getFullYear() - 1980) << 9) | ((agora.getMonth() + 1) << 5) | agora.getDate();
  const partes = [];
  const central = [];
  let offset = 0;
  const cab = (tamanho) => { const b = new Uint8Array(tamanho); return [b, new DataView(b.buffer)]; };

  arquivos.forEach(({ nome, dados }) => {
    const nomeB = utf8(nome);
    const crc = crc32(dados);
    const [local, l] = cab(30 + nomeB.length);
    l.setUint32(0, 0x04034b50, true); l.setUint16(4, 20, true); l.setUint16(6, 0x0800, true);
    l.setUint16(8, 0, true); l.setUint16(10, hora, true); l.setUint16(12, dia, true);
    l.setUint32(14, crc, true); l.setUint32(18, dados.length, true); l.setUint32(22, dados.length, true);
    l.setUint16(26, nomeB.length, true); l.setUint16(28, 0, true);
    local.set(nomeB, 30);

    const [dir, c] = cab(46 + nomeB.length);
    c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true);
    c.setUint16(8, 0x0800, true); c.setUint16(10, 0, true); c.setUint16(12, hora, true);
    c.setUint16(14, dia, true); c.setUint32(16, crc, true); c.setUint32(20, dados.length, true);
    c.setUint32(24, dados.length, true); c.setUint16(28, nomeB.length, true);
    c.setUint32(42, offset, true);
    dir.set(nomeB, 46);

    partes.push(local, dados);
    central.push(dir);
    offset += local.length + dados.length;
  });

  const tamanhoCentral = central.reduce((s, b) => s + b.length, 0);
  const [fim, f] = cab(22);
  f.setUint32(0, 0x06054b50, true);
  f.setUint16(8, arquivos.length, true); f.setUint16(10, arquivos.length, true);
  f.setUint32(12, tamanhoCentral, true); f.setUint32(16, offset, true);

  const todos = [...partes, ...central, fim];
  const saida = new Uint8Array(todos.reduce((s, b) => s + b.length, 0));
  let pos = 0;
  todos.forEach((b) => { saida.set(b, pos); pos += b.length; });
  return saida;
}

// Caracteres de controle quebram o XML (o Excel recusa o arquivo inteiro).
// eslint-disable-next-line no-control-regex
const xml = (v) => String(v).replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function colunaExcel(indice) {
  let n = indice + 1;
  let nome = "";
  while (n > 0) { const r = (n - 1) % 26; nome = String.fromCharCode(65 + r) + nome; n = Math.floor((n - 1) / 26); }
  return nome;
}

/** Bytes de um .xlsx com uma aba: primeira linha em negrito e congelada, filtro ligado. */
export function gerarXlsx(linhas, { aba = "Planilha", larguras = [] } = {}) {
  const totalColunas = Math.max(1, ...linhas.map((l) => l.length));
  const celula = (valor, col, lin, estilo) => {
    const ref = `${colunaExcel(col)}${lin}`;
    const s = estilo ? ` s="${estilo}"` : "";
    if (valor === null || valor === undefined || valor === "") return "";
    if (typeof valor === "number" && Number.isFinite(valor)) return `<c r="${ref}"${s}><v>${valor}</v></c>`;
    return `<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${xml(valor)}</t></is></c>`;
  };
  const corpo = linhas.map((linha, i) => `<row r="${i + 1}">${linha.map((v, c) => celula(v, c, i + 1, i === 0 ? 1 : 0)).join("")}</row>`).join("");
  const cols = Array.from({ length: totalColunas }, (_, i) =>
    `<col min="${i + 1}" max="${i + 1}" width="${larguras[i] || 18}" customWidth="1"/>`).join("");
  const ultima = `${colunaExcel(totalColunas - 1)}${Math.max(1, linhas.length)}`;
  const nomeAba = xml(String(aba).replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Planilha");

  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><cols>${cols}</cols><sheetData>${corpo}</sheetData><autoFilter ref="A1:${ultima}"/></worksheet>`;
  const arquivos = {
    "[Content_Types].xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`,
    "_rels/.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    "xl/workbook.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${nomeAba}" sheetId="1" r:id="rId1"/></sheets><definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">'${nomeAba.replace(/'/g, "''")}'!$A$1:$${colunaExcel(totalColunas - 1)}$${Math.max(1, linhas.length)}</definedName></definedNames></workbook>`,
    "xl/_rels/workbook.xml.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    "xl/styles.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFC4005A"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`,
    "xl/worksheets/sheet1.xml": sheet,
  };
  return zipArmazenado(Object.entries(arquivos).map(([nome, conteudo]) => ({ nome, dados: utf8(conteudo) })));
}

/** CSV no padrão do Excel brasileiro: `;` como separador e BOM para abrir com acento. */
export function gerarCsv(linhas) {
  const campo = (v) => {
    const t = v === null || v === undefined ? "" : String(v);
    return /[";\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  return "﻿" + linhas.map((l) => l.map(campo).join(";")).join("\r\n");
}

export const TIPO_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function baixarArquivo(conteudo, nome, tipo) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
